import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { useI18n } from '../i18n';
import { PropertyCard } from '../components/property/PropertyCard';
import { FilterSidebar } from '../components/property/FilterSidebar';
import { InteractiveMap } from '../components/property/InteractiveMap';
import { Pagination } from '../components/common/Pagination';
import { REGIONS } from '../data/mockData';
import { Loader2 } from 'lucide-react';

export function OffersPage() {
  const { getFilteredProperties, filters, properties: allProperties, clearFilters, propertiesLoading, propertiesLoaded, loadPropertiesFromApi } = useAppStore();
  const { language, t } = useI18n();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [mapVisibleProperties, setMapVisibleProperties] = useState<ReturnType<typeof getFilteredProperties> | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Load properties from API if not loaded
  useEffect(() => {
    if (!propertiesLoaded && !propertiesLoading) {
      loadPropertiesFromApi();
    }
  }, [propertiesLoaded, propertiesLoading, loadPropertiesFromApi]);

  const properties = getFilteredProperties();
  
  // Active properties for location search (only approved and active)
  const activeListings = useMemo(() => 
    allProperties.filter(p => p.status === 'active' && p.approved), 
    [allProperties]
  );
  
  // Calculate focusLocation based on selected locations
  const focusLocation = useMemo(() => {
    // If we have locations array, use the first one
    if (filters.locations && filters.locations.length > 0) {
      const firstLoc = filters.locations[0];
      if (firstLoc.lat && firstLoc.lng) {
        return { lat: firstLoc.lat, lng: firstLoc.lng, zoom: firstLoc.zoom || 11 };
      }
    }
    // Fallback to legacy locationLat/locationLng
    if (filters.locationLat && filters.locationLng) {
      return { lat: filters.locationLat, lng: filters.locationLng, zoom: 13 };
    }
    // If a region is selected, find its coordinates
    if (filters.region) {
      const region = REGIONS.find(r => r.name === filters.region);
      if (region) {
        if (filters.city) {
          const city = region.cities.find(c => c.name === filters.city);
          if (city?.lat && city?.lng) {
            return { lat: city.lat, lng: city.lng, zoom: 13 };
          }
        }
        if (region.lat && region.lng) {
          return { lat: region.lat, lng: region.lng, zoom: region.zoom || 10 };
        }
      }
    }
    return undefined;
  }, [filters.locations, filters.region, filters.city, filters.locationLat, filters.locationLng]);
  
  // Filter by map bounds if map view is active and we have visible properties
  const displayedProperties = (viewMode === 'map' && mapVisibleProperties) 
    ? mapVisibleProperties 
    : properties;

  const sortedProperties = [...displayedProperties].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'sqm-asc':
        return a.squareMeters - b.squareMeters;
      case 'sqm-desc':
        return b.squareMeters - a.squareMeters;
      case 'views':
        return b.views - a.views;
      default:
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
  });

  const sortOptions = [
    { value: 'newest', label: language === 'sr' ? 'Najnovije' : 'Newest' },
    { value: 'price-asc', label: language === 'sr' ? 'Cena: rastuće' : 'Price: Low to High' },
    { value: 'price-desc', label: language === 'sr' ? 'Cena: opadajuće' : 'Price: High to Low' },
    { value: 'sqm-asc', label: language === 'sr' ? 'Kvadratura: rastuće' : 'Area: Low to High' },
    { value: 'sqm-desc', label: language === 'sr' ? 'Kvadratura: opadajuće' : 'Area: High to Low' },
    { value: 'views', label: language === 'sr' ? 'Najpopularnije' : 'Most Popular' },
  ];

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy]);

  // Pagination
  const totalItems = sortedProperties.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedProperties = viewMode === 'map' ? sortedProperties : sortedProperties.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.keys(filters).length > 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-8 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            {language === 'sr' ? 'Ponuda nekretnina' : 'Property Listings'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {language === 'sr' 
              ? 'Pronađite savršenu nekretninu za kupovinu ili iznajmljivanje' 
              : 'Find the perfect property to buy or rent'}
          </p>
        </div>

        <div className="flex gap-6">
          {/* Desktop Filters */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <FilterSidebar 
              showPricePerUnit={true} 
              listings={activeListings}
              pageType="offers"
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setMobileFiltersOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  {t.filters.title}
                </button>
                <span className="text-slate-600 dark:text-slate-400">
                  {sortedProperties.length} {language === 'sr' 
                    ? (sortedProperties.length === 1 ? 'rezultat' : 'rezultata')
                    : (sortedProperties.length === 1 ? 'result' : 'results')}
                </span>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-amber-600 hover:text-amber-500 text-sm whitespace-nowrap"
                  >
                    {t.filters.clearFilters}
                  </button>
                )}
              </div>

              <div className="flex items-center gap-4">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                {/* View mode toggle - visible on all screen sizes */}
                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Mreža' : 'Grid'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Lista' : 'List'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'map' ? 'bg-white dark:bg-slate-600 shadow-sm text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                    title={language === 'sr' ? 'Mapa' : 'Map'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Results */}
            {propertiesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                <span className="ml-3 text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Učitavanje...' : 'Loading...'}
                </span>
              </div>
            ) : viewMode === 'map' ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden">
                <InteractiveMap 
                  properties={properties} 
                  height="600px" 
                  showFilters={false} 
                  onVisiblePropertiesChange={setMapVisibleProperties}
                  focusLocation={focusLocation}
                  selectedRegion={filters.region}
                />
              </div>
            ) : sortedProperties.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-12 text-center"
              >
                <svg className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{t.common.noResults}</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  {language === 'sr' ? 'Pokušajte sa drugim kriterijumima pretrage' : 'Try different search criteria'}
                </p>
              </motion.div>
            ) : (
              <div className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
                  : 'space-y-4'
              }>
                {paginatedProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    variant={viewMode === 'list' ? 'horizontal' : 'default'}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {viewMode !== 'map' && sortedProperties.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalItems}
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      <AnimatePresence>
        {mobileFiltersOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileFiltersOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-80 bg-white dark:bg-slate-800 z-50 overflow-y-auto lg:hidden"
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t.filters.title}</h2>
                  <button
                    onClick={() => setMobileFiltersOpen(false)}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-600 dark:text-slate-400"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <FilterSidebar 
                  showPricePerUnit={true} 
                  onClose={() => setMobileFiltersOpen(false)}
                  listings={activeListings}
                  pageType="offers"
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
